import React, { useEffect, useRef, useState } from "react"
import ResizeObserver from "resize-observer-polyfill"
import useAppContext from "~/hooks/useAppContext"
import Loading from "./components/Loading"
import { editorFonts } from "./constants/fonts"
import { getFonts } from "./store/slices/fonts/actions"
import { getPixabayResources } from "./store/slices/resources/actions"
import { useAppDispatch } from "./store/store"
import { getPremiumProject, getProjectForDownload, getSavedProject, getSkuData, listImagesAtPath } from "./services/supabase"
import Cookies from 'js-cookie';
import { nanoid } from "@reduxjs/toolkit"
import { clearEnvCookie, getAppEnv, setEnvCookie } from "~/utils/env";

const hasRenderableScenes = (design: any) =>
  Array.isArray(design?.scenes) && design.scenes.length > 0

const parsePremiumRequest = (value: any) => {
  if (!value) return null
  if (typeof value === "object") return value

  try {
    return JSON.parse(value)
  } catch (error) {
    console.error("Could not parse premium request", error)
    return null
  }
}

const mapPremiumUploads = (uploads: any[] = []) =>
  uploads
    .filter((upload) => upload?.url)
    .map((upload) => ({
      id: upload.path || upload.url || nanoid(),
      src: upload.url,
      preview: upload.url,
      type: "StaticImage",
    }))

function Container({ children }: { children: React.ReactNode }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const { setIsMobile, setIsTablet,setUploads, setSkuData } = useAppContext()
  const [loaded, setLoaded] = useState(false)
  const dispatch = useAppDispatch()
  const updateMediaQuery = (value: number) => {
    if (value >= 800) {
      setIsMobile(false)
      setIsTablet(false)
    } else if (value >= 600 && value < 800) {
      setIsMobile(false)
      setIsTablet(true)
    } else {
      setIsMobile(true)
      setIsTablet(false)
    }
  }
  useEffect(() => {
    const containerElement = containerRef.current!
    const containerWidth = containerElement.clientWidth
    updateMediaQuery(containerWidth)
    const resizeObserver = new ResizeObserver((entries) => {
      const { width = containerWidth } = (entries[0] && entries[0].contentRect) || {}
      updateMediaQuery(width)
    })
    resizeObserver.observe(containerElement)
    return () => {
      if (containerElement) {
        resizeObserver.unobserve(containerElement)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const initialize = async () => {
      try {
        dispatch(getFonts())
        dispatch(getPixabayResources())
        loadFonts(),
        await loadOrSetActiveDesignId()
        await loadUploads()
        setLoaded(true)
      } catch (error) {
        console.error("Error during initialization:", error)
      }
    }
  
    initialize()
  }, [])
  //TODO: Load uploads from supabase and other init data
  const loadUploads = async () => {
    const premiumMode = Cookies.get('premiumMode') === 'true'
    const premiumUploads = localStorage.getItem('premiumUploads')

    if (premiumMode && premiumUploads) {
      setUploads(JSON.parse(premiumUploads))
      return
    }

    const activePath = `projects/${Cookies.get('premiumDesignId') || Cookies.get('activeDesignId') || ''}`

    if (!activePath || activePath.endsWith('/')) {
      setUploads([])
      return
    }

    const uploads = await listImagesAtPath(activePath)
    setUploads(uploads || [])
  }

  const loadSkuData = async () => {
    const photobookSku = Cookies.get('photobookSku')

    if (Cookies.get('customerId') != null) {
      localStorage.setItem('customerId', Cookies.get('customerId'))
    }
    else {
      Cookies.remove('customerId')
      localStorage.removeItem('customerId')
    }
    //console.log("photobookSku", photobookSku)
    const skuData = await getSkuData(photobookSku)
    //console.log("skuData", skuData)
    const { envName, shopBaseUrl } = getAppEnv();
    const isProduction = envName === "production";

    if (!skuData && isProduction) {
      window.location.href = shopBaseUrl;
    }
    setSkuData(skuData)
  }

  const loadOrSetActiveDesignId = async () => {
    //get path from url
    const path = window.location.pathname
    let designId = path.split("/").pop()
    let isDownload = false;
    let isPremium = false;

    // Detect explicit new-order signal from the shop (Form.jsx appends ?new=1).
    const urlParams = new URLSearchParams(window.location.search)
    const isNewOrder = urlParams.get('new') === '1'

    if (isNewOrder) {
      // Explicit new order from the shop — clear everything and start fresh.
      window.history.replaceState({}, '', '/')
      Cookies.set('isDownload', isDownload);
      clearEnvCookie('premiumMode');
      clearEnvCookie('premiumOrderItemId');
      clearEnvCookie('premiumDesignId');
      localStorage.removeItem('savedProject');
      localStorage.removeItem('currentSessionDesign');
      localStorage.removeItem('premiumUploads');
      localStorage.removeItem('activeDesignId');
      localStorage.removeItem('extras');
      await loadSkuData();
      return
    }

    if (!designId || designId === "/") {
      const premiumOrderItemId = Cookies.get('premiumOrderItemId')
      if (premiumOrderItemId) {
        designId = premiumOrderItemId
        isPremium = true
        Cookies.set('premiumMode', 'true')
      } else if (!localStorage.getItem('activeDesignId')) {
        // Only clear project data if no active design exists.
        // If activeDesignId is set, Container already loaded the project
        // on the initial visit and replaceState'd the URL to "/".
        Cookies.set('isDownload', isDownload);
        clearEnvCookie('premiumMode');
        clearEnvCookie('premiumOrderItemId');
        clearEnvCookie('premiumDesignId');
        // Clear stale project data so a fresh template is generated
        localStorage.removeItem('savedProject');
        localStorage.removeItem('currentSessionDesign');
        localStorage.removeItem('premiumUploads');
        localStorage.removeItem('activeDesignId');
        await loadSkuData();
        return
      } else {
        // Path is "/" but we have a saved activeDesignId — load SKU and let
        // Scenes.tsx pick up the project from localStorage.
        await loadSkuData();
        return
      }
    }

    if (designId.includes("download-")) {
      designId = designId.replace("download-", "")
      isDownload = true;
      clearEnvCookie('premiumMode');
    } else if (designId.includes("premium-")) {
      designId = designId.replace("premium-", "")
      isPremium = true;
      Cookies.set('premiumMode', 'true');
      Cookies.set('premiumOrderItemId', designId);
      window.history.replaceState({}, "", "/");
    } else if (isPremium) {
      window.history.replaceState({}, "", "/");
    } else {
      Cookies.set('isDownload', isDownload);
      clearEnvCookie('premiumMode');
      clearEnvCookie('premiumOrderItemId');
      clearEnvCookie('premiumDesignId');
      await loadSkuData()
      // Clean the URL so Scenes.tsx does not trigger a full page reload
      window.history.replaceState({}, "", "/");
    }

    Cookies.set('isDownload', isDownload);

    const project = isPremium
      ? await getPremiumProject(designId)
      : isDownload
        ? await getProjectForDownload(designId)
        : await getSavedProject(designId, Cookies.get('customerId'))

     if (isPremium && project?.product_sku) {
       setEnvCookie('photobookSku', project.product_sku)
       if (project.premium_design_id) {
         setEnvCookie('premiumDesignId', project.premium_design_id)
         setEnvCookie('activeDesignId', project.premium_design_id)
       }
       await loadSkuData()
     }

     if (project && designId) {
       // For downloads, project is { design: {...} } from order_item table
       // For regular saves, project is { design_id, design, ... } from projects table
       const designData = project.design

       console.log("Loaded project data:", { 
         isDownload, 
         designId, 
         hasDesign: !!designData,
         scenesCount: designData?.scenes?.length || 0 
       })
       
       // Determine the canonical design ID for this session.
       const activeId = isPremium
         ? (project.premium_design_id || designId)
         : isDownload
           ? (designData?.id || designId)
           : (project.design_id || designId)

       // Ensure the design JSON id matches the activeId so Scenes.tsx can
       // correlate localStorage with the cookie reliably.
       if (designData && designData.id !== activeId) {
         designData.id = activeId
       }

       // Save project design to local storage
       if (hasRenderableScenes(designData)) {
         localStorage.setItem('savedProject', JSON.stringify(designData))
         localStorage.removeItem('currentSessionDesign')
       } else {
         localStorage.removeItem('savedProject')
         localStorage.removeItem('currentSessionDesign')
       }

       if (isPremium) {
         const premiumRequest = parsePremiumRequest(project.premium_request)
         const premiumUploads = mapPremiumUploads(premiumRequest?.uploads || [])

         if (premiumUploads.length > 0) {
           localStorage.setItem('premiumUploads', JSON.stringify(premiumUploads))
           setUploads(premiumUploads)
         } else {
           localStorage.removeItem('premiumUploads')
         }
       } else {
         localStorage.removeItem('premiumUploads')
       }

       setEnvCookie('activeDesignId', activeId)
       // Also persist in localStorage as a same-origin fallback for cookie
       // domain issues (e.g. stale cookies on a more specific subdomain).
       localStorage.setItem('activeDesignId', activeId)
       
       console.log("Set activeDesignId cookie to:", activeId)
       await loadUploads()
     } else {
       console.warn("Project not found for designId:", designId)
       localStorage.removeItem('premiumUploads')
     }
  }

  const getPixabayResources2 = () => {
    dispatch(getPixabayResources())
  }

  const loadFonts = () => {
    const promisesList = editorFonts.map((font) => {
      // @ts-ignore
      return new FontFace(font.name, `url(${font.url})`, font.options).load().catch((err) => err)
    })
    Promise.all(promisesList)
      .then((res) => {
        res.forEach((uniqueFont) => {
          if (uniqueFont && uniqueFont.family) {
            document.fonts.add(uniqueFont)
          }
        })
      })
      .catch((err) => console.log({ err }))
  }

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        display: "flex",
        height: "100vh",
        width: "100vw",
      }}
    >
      {loaded ? <>{children} </> : <Loading />}
    </div>
  )
}

export default Container
